import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Star = {
  x: number;
  y: number;
  r: number;
  base: number; // base opacity
  amp: number; // twinkle amplitude
  speed: number; // twinkle speed
  phase: number;
  gold: boolean;
  pulseAt: number; // ms timestamp of next slow pulse
};

type ShootingStar = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number; // 0..1
  len: number;
};

export function Starfield() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "starfield_enabled")
        .maybeSingle();
      if (cancelled) return;
      // Default to enabled if setting missing; disable only when explicitly false.
      setEnabled(data?.value === false ? false : true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let stars: Star[] = [];
    let shooting: ShootingStar | null = null;
    let nextShootAt = performance.now() + 25000 + Math.random() * 30000;
    let driftX = 0;
    let driftY = 0;
    let rafId = 0;
    let running = true;

    const seed = () => {
      const isMobile = width < 768;
      const count = isMobile ? 55 : 110;
      stars = new Array(count).fill(0).map(() => {
        const r = Math.random();
        const size =
          r < 0.75
            ? 0.3 + Math.random() * 0.45
            : r < 0.95
              ? 0.7 + Math.random() * 0.5
              : 1.2 + Math.random() * 0.7;
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          r: size,
          base: 0.2 + Math.random() * 0.35,
          amp: 0.1 + Math.random() * 0.28,
          speed: 0.08 + Math.random() * 0.35, // rad/s (slower)
          phase: Math.random() * Math.PI * 2,
          gold: Math.random() < 0.12,
          pulseAt: performance.now() + 4000 + Math.random() * 18000,
        };
      });
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const drawStar = (s: Star, t: number, now: number) => {
      const twinkle = reduce ? 0 : Math.sin(t * s.speed + s.phase) * s.amp;
      let pulseBoost = 0;
      if (!reduce && now >= s.pulseAt) {
        const elapsed = now - s.pulseAt;
        const dur = 2200;
        if (elapsed < dur) {
          const p = elapsed / dur;
          pulseBoost = Math.sin(p * Math.PI) * 0.35;
        } else {
          s.pulseAt = now + 8000 + Math.random() * 22000;
        }
      }
      const alpha = Math.max(0.04, Math.min(1, s.base + twinkle + pulseBoost));
      const x = (s.x + driftX) % width;
      const y = (s.y + driftY) % height;
      const px = x < 0 ? x + width : x;
      const py = y < 0 ? y + height : y;

      // Halo for larger stars
      if (s.r > 0.95) {
        const haloR = s.r * (s.r > 1.3 ? 7 : 4.5);
        const grad = ctx.createRadialGradient(px, py, 0, px, py, haloR);
        const glow = s.gold ? "255, 210, 130" : "255, 240, 215";
        grad.addColorStop(0, `rgba(${glow}, ${alpha * 0.45})`);
        grad.addColorStop(1, `rgba(${glow}, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(px, py, haloR, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.fillStyle = s.gold
        ? `rgba(255, 214, 140, ${alpha})`
        : `rgba(250, 244, 228, ${alpha})`;
      ctx.beginPath();
      ctx.arc(px, py, s.r, 0, Math.PI * 2);
      ctx.fill();
    };

    const spawnShooting = () => {
      const startX = Math.random() * width * 0.7;
      const startY = Math.random() * height * 0.4;
      const speed = 6 + Math.random() * 4;
      shooting = {
        x: startX,
        y: startY,
        vx: speed,
        vy: speed * 0.35,
        life: 0,
        len: 80 + Math.random() * 60,
      };
    };

    const drawShooting = (dt: number) => {
      if (!shooting) return;
      shooting.x += shooting.vx;
      shooting.y += shooting.vy;
      shooting.life += dt / 1200;
      if (shooting.life >= 1 || shooting.x > width + 100 || shooting.y > height + 100) {
        shooting = null;
        nextShootAt = performance.now() + 35000 + Math.random() * 40000;
        return;
      }
      const alpha = shooting.life < 0.2 ? shooting.life / 0.2 : 1 - (shooting.life - 0.2) / 0.8;
      const tailX = shooting.x - shooting.vx * (shooting.len / speedMag(shooting));
      const tailY = shooting.y - shooting.vy * (shooting.len / speedMag(shooting));
      const grad = ctx.createLinearGradient(tailX, tailY, shooting.x, shooting.y);
      grad.addColorStop(0, "rgba(255, 214, 140, 0)");
      grad.addColorStop(1, `rgba(255, 230, 180, ${alpha})`);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.4;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(shooting.x, shooting.y);
      ctx.stroke();
    };

    const speedMag = (s: ShootingStar) => Math.hypot(s.vx, s.vy);

    let last = performance.now();
    const frame = (now: number) => {
      if (!running) return;
      const dt = now - last;
      last = now;

      if (!reduce) {
        const sway = Math.sin(now / 12000) * 0.008;
        driftX += 0.008 + sway;
        driftY += 0.003;
      }

      ctx.clearRect(0, 0, width, height);
      const t = now / 1000;
      for (const s of stars) drawStar(s, t, now);

      if (!reduce) {
        if (!shooting && now >= nextShootAt) spawnShooting();
        drawShooting(dt);
      }

      rafId = requestAnimationFrame(frame);
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        running = false;
        cancelAnimationFrame(rafId);
      } else if (!running) {
        running = true;
        last = performance.now();
        rafId = requestAnimationFrame(frame);
      }
    };

    resize();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    rafId = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        pointerEvents: "none",
        overflow: "hidden",
        mixBlendMode: "screen",
      }}
    >
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
    </div>
  );
}